import { Component, OnInit } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import Swal from 'sweetalert2';
import {
  R4Service,
  R4Operation,
  R4Stats,
  R4Monto,
  R4Health,
} from '../../services/r4.service';
import { RegistrarReferenciaOperadorModalComponent } from '../referencias-ves-admin/registrar-referencia-operador-modal/registrar-referencia-operador-modal.component';
import { R4MontoMinModalComponent } from './r4-monto-min-modal/r4-monto-min-modal.component';

@Component({
  selector: 'app-r4-home',
  templateUrl: './r4-home.component.html',
  styles: [],
})
export class R4HomeComponent implements OnInit {
  loading = false;
  error: string | null = null;

  pagoR4Activo = false;
  loadingToggle = false;

  stats: R4Stats | null = null;
  monto: R4Monto | null = null;
  health: R4Health | null = null;
  healthLoading = false;

  operations: R4Operation[] = [];
  filterStatus = '';
  filterType = '';

  readonly statusOptions = ['', 'CREATED', 'SENT', 'PENDING', 'COMPLETED', 'REJECTED', 'TIMEOUT', 'ERROR'];

  constructor(
    private r4Service: R4Service,
    private dialog: MatDialog,
  ) {}

  ngOnInit(): void {
    this.loadAll();
  }

  loadAll(): void {
    this.loadHealth();
    this.loadToggle();
    this.loadStats();
    this.loadMonto();
    this.loadOperations();
  }

  private loadHealth(): void {
    this.healthLoading = true;
    this.r4Service.getHealth().subscribe({
      next: (h) => {
        this.health = h;
        this.healthLoading = false;
      },
      error: () => {
        this.health = { operational: false, tasaBcv: null, error: 'No disponible', timestamp: '' };
        this.healthLoading = false;
      },
    });
  }

  private loadToggle(): void {
    this.r4Service.getPagoR4Status().subscribe({
      next: (r) => (this.pagoR4Activo = r.active),
      error: () => {},
    });
  }

  private loadStats(): void {
    this.r4Service.getStats().subscribe({
      next: (s) => (this.stats = s),
      error: () => {},
    });
  }

  private loadMonto(): void {
    this.r4Service.getMonto().subscribe({
      next: (m) => (this.monto = m),
      error: () => {},
    });
  }

  loadOperations(): void {
    this.loading = true;
    this.error = null;
    this.r4Service
      .getOperations({
        status: this.filterStatus || undefined,
        type: this.filterType || undefined,
        limit: 100,
      })
      .subscribe({
        next: (ops) => {
          this.operations = ops || [];
          this.loading = false;
        },
        error: (err) => {
          this.error = typeof err === 'string' ? err : 'Error al cargar operaciones R4';
          this.loading = false;
        },
      });
  }

  toggle(): void {
    const nuevo = !this.pagoR4Activo;
    Swal.fire({
      icon: 'question',
      title: `¿${nuevo ? 'Activar' : 'Desactivar'} pagos R4?`,
      showCancelButton: true,
      confirmButtonText: 'Sí',
      cancelButtonText: 'Cancelar',
      confirmButtonColor: nuevo ? '#16a34a' : '#dc2626',
    }).then((res) => {
      if (!res.isConfirmed) return;
      this.loadingToggle = true;
      this.r4Service.togglePagoR4().subscribe({
        next: (r) => {
          this.pagoR4Activo = r.active;
          this.loadingToggle = false;
        },
        error: () => (this.loadingToggle = false),
      });
    });
  }

  pollOperation(op: R4Operation): void {
    this.r4Service.pollOperation(op.id).subscribe({
      next: () => {
        this.loadOperations();
        this.loadStats();
      },
      error: (err) => {
        Swal.fire({ icon: 'error', title: 'Error', text: typeof err === 'string' ? err : 'No se pudo consultar' });
      },
    });
  }

  openDetail(op: R4Operation): void {
    Swal.fire({
      title: `Operación R4 ${op.operationType}`,
      html: `
        <div style="text-align:left; font-size:0.8rem;">
          <p><b>Estado:</b> ${op.status}</p>
          <p><b>Referencia:</b> ${op.referencia}</p>
          <p><b>External ID:</b> ${op.externalId || '-'}</p>
          <p><b>Monto:</b> ${this.formatVES(op.monto)}</p>
          <p><b>Cédula:</b> ${op.cedula || '-'} &nbsp; <b>Tel/Cuenta:</b> ${op.telefono || op.cuenta || '-'}</p>
          <p><b>Banco:</b> ${op.codigoBanco || '-'}</p>
          <p><b>Código:</b> ${op.codigoRespuesta || op.codigoIso || '-'} &nbsp; <b>Msg:</b> ${op.mensajeRespuesta || '-'}</p>
          <p><b>Tx:</b> ${op.transaccionId ?? '-'} &nbsp; <b>Cardex:</b> ${op.walletCardexVesId || '-'}</p>
          <p><b>Polls:</b> ${op.pollCount} &nbsp; <b>Creada:</b> ${op.createdAt}</p>
        </div>`,
      confirmButtonText: 'Cerrar',
      confirmButtonColor: '#1C593D',
      width: 600,
    });
  }

  openMontoModal(): void {
    const ref = this.dialog.open(R4MontoMinModalComponent, {
      data: { montoMin: this.monto?.montoMin ?? 0, montoMax: this.monto?.montoMax ?? 500000 },
      width: '440px',
    });
    ref.afterClosed().subscribe((ok) => {
      if (ok) this.loadMonto();
    });
  }

  openRegistrarOperadorModal(): void {
    const ref = this.dialog.open(RegistrarReferenciaOperadorModalComponent, {
      data: { lockedRail: 'R4' },
      maxWidth: '826px',
      minWidth: '500px',
    });
    ref.afterClosed().subscribe((result) => {
      if (result) {
        this.loadOperations();
        this.loadStats();
      }
    });
  }

  formatVES(amount: number | null | undefined): string {
    if (amount === null || amount === undefined) return '-';
    return new Intl.NumberFormat('es-VE', {
      style: 'currency',
      currency: 'VES',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(Number(amount));
  }

  statusColor(status: string): string {
    if (status === 'COMPLETED') return '#16a34a';
    if (status === 'REJECTED' || status === 'ERROR' || status === 'TIMEOUT') return '#dc2626';
    return '#d97706'; // pending/sent/created
  }
}
